import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError } from '@/lib/api'
import { formatQuantity, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal, Select, Toggle } from '@/components/ui/primitives'
import { t } from '@/i18n'

interface SaleLine {
  id: number
  productId: number
  productName: string
  sku: string | null
  quantity: number
  unitPriceUsd: number
  grossUsd: number
  refundedQuantity: number
  refundableQuantity: number
}

interface LineDraft {
  quantity: string
  restock: boolean
  expiryDate: string
  wasteReason: string
}

export function RefundDialog({
  saleId,
  saleNumber,
  registerId,
  onClose,
}: {
  saleId: number | null
  saleNumber: string | null
  registerId: number | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState<Record<number, LineDraft>>({})
  const [reason, setReason] = useState('')
  const [method, setMethod] = useState('CashUsd')
  const [error, setError] = useState<string | null>(null)

  const lines = useQuery({
    queryKey: ['sale-lines', saleId],
    queryFn: () => apiClient.get<SaleLine[]>(`/api/sales/${saleId}/lines`),
    enabled: saleId !== null,
  })

  useEffect(() => {
    if (saleId !== null) {
      setDrafts({})
      setReason('')
      setMethod('CashUsd')
      setError(null)
    }
  }, [saleId])

  const submit = useMutation({
    mutationFn: () =>
      apiClient.post('/api/refunds', {
        // A retry carries the same key, so a dropped response cannot pay out twice.
        clientReference: crypto.randomUUID(),
        saleId,
        registerId,
        reason: reason.trim(),
        method,
        lines: Object.entries(drafts)
          .filter(([, draft]) => Number(draft.quantity) > 0)
          .map(([lineId, draft]) => ({
            saleLineId: Number(lineId),
            quantity: Number(draft.quantity),
            restock: draft.restock,
            expiryDate: draft.restock && draft.expiryDate ? draft.expiryDate : null,
            wasteReason: draft.restock ? null : draft.wasteReason,
          })),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sales'] })
      await queryClient.invalidateQueries({ queryKey: ['products'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
      onClose()
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not refund.'),
  })

  function update(lineId: number, patch: Partial<LineDraft>) {
    setDrafts((current) => ({
      ...current,
      [lineId]: {
        quantity: '',
        restock: true,
        expiryDate: '',
        wasteReason: 'Damaged',
        ...current[lineId],
        ...patch,
      },
    }))
  }

  const total = (lines.data ?? []).reduce((sum, line) => {
    const qty = Number(drafts[line.id]?.quantity ?? 0)
    // Indicative only — the server prices the refund from the original line.
    return sum + (line.quantity > 0 ? (line.grossUsd / line.quantity) * qty : 0)
  }, 0)

  const hasSomething = Object.values(drafts).some((draft) => Number(draft.quantity) > 0)

  return (
    <Modal
      open={saleId !== null}
      onClose={onClose}
      wide
      title={`Refund ${saleNumber ?? ''}`}
      description={t('Items are refunded at the price they were sold for, not today\'s price.')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => submit.mutate()}
            disabled={!hasSomething || !reason.trim() || submit.isPending}
          >
            {submit.isPending ? 'Refunding…' : `Refund ${formatUsd(total)}`}
          </Button>
        </>
      }
    >
      <ul className="flex flex-col gap-3">
        {lines.data?.map((line) => {
          const draft = drafts[line.id]
          const exhausted = line.refundableQuantity <= 0

          return (
            <li
              key={line.id}
              className={`surface-raised rounded-[var(--radius-card)] border p-3 ${exhausted ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{line.productName}</p>
                  <p className="tabular text-xs text-[var(--text-secondary)]">
                    sold {formatQuantity(line.quantity)} × {formatUsd(line.unitPriceUsd)}
                    {line.refundedQuantity > 0 &&
                      ` · ${formatQuantity(line.refundedQuantity)} already returned`}
                  </p>
                </div>
                <Input
                  aria-label={`Quantity to return for ${line.productName}`}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max={line.refundableQuantity}
                  step="0.001"
                  disabled={exhausted}
                  value={draft?.quantity ?? ''}
                  onChange={(e) => update(line.id, { quantity: e.target.value })}
                  placeholder="0"
                  className="tabular w-24 text-right"
                />
              </div>

              {Number(draft?.quantity) > 0 && (
                <div className="mt-3 border-t border-[var(--border-subtle)] pt-3">
                  <Toggle
                    checked={draft?.restock ?? true}
                    onChange={(v) => update(line.id, { restock: v })}
                    label={t('Fit to sell again')}
                    hint={
                      draft?.restock
                        ? 'Goes back into stock as a new batch'
                        : 'Comes back then is written off as waste'
                    }
                  />

                  {draft?.restock ? (
                    <Input
                      label={t('Expiry date')}
                      type="date"
                      value={draft?.expiryDate ?? ''}
                      onChange={(e) => update(line.id, { expiryDate: e.target.value })}
                      wrapperClassName="mt-3"
                      hint={t('Required for anything that tracks expiry')}
                    />
                  ) : (
                    <Select
                      label={t('Write-off reason')}
                      value={draft?.wasteReason ?? 'Damaged'}
                      onChange={(e) => update(line.id, { wasteReason: e.target.value })}
                      wrapperClassName="mt-3"
                    >
                      <option value="Damaged">{t('Damaged')}</option>
                      <option value="Expired">{t('Expired')}</option>
                      <option value="CountShortage">{t('Unsellable')}</option>
                    </Select>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Select label={t('Refund by')} value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="CashUsd">{t('Cash — US dollars')}</option>
          <option value="CashLbp">{t('Cash — Lebanese lira')}</option>
          <option value="Card">{t('Card')}</option>
          <option value="OnAccount">{t('Credit to account')}</option>
          <option value="Voucher">{t('Store credit')}</option>
        </Select>
        <Input
          label={t('Reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('Why is it coming back?')}
          required
        />
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          {error}
        </p>
      )}
    </Modal>
  )
}
